import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Fade,
  CircularProgress,
  Paper,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailIcon from '@mui/icons-material/Email';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Styled components
const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

const CodeInput = styled(TextField)(({ theme }) => ({
  '& input': {
    fontSize: '1.5rem',
    textAlign: 'center',
    letterSpacing: '0.5rem',
    fontWeight: 'bold'
  }
}));

const MethodButton = styled(Button)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderColor: selected ? theme.palette.primary.main : theme.palette.grey[300],
  borderWidth: selected ? 2 : 1,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
}));

const TimerText = styled(Typography)(({ theme, warning }) => ({
  color: warning ? theme.palette.warning.main : theme.palette.text.secondary,
  fontWeight: warning ? 'bold' : 'normal'
}));

// Helper function to format time
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// Alpha function for styled components
const alpha = (color, opacity) => {
  return color + Math.round(opacity * 255).toString(16).padStart(2, '0');
};

const TwoFactorAuth = ({ onVerify, onResendCode, loading, error, clearError, userEmail }) => {
  // State
  const [verificationCode, setVerificationCode] = useState('');
  const [formError, setFormError] = useState('');
  const [method, setMethod] = useState('email'); // 'email' or 'sms'
  const [timer, setTimer] = useState(120); // 2 minutes countdown
  const [canResend, setCanResend] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('***-***-1234'); // Would come from user data
  
  // Refs
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Focus on input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Start countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);
  
  // Handle verification code change
  const handleCodeChange = (e) => {
    const value = e.target.value;
    
    // Only allow numbers and limit to 6 digits
    if (/^\d*$/.test(value) && value.length <= 6) {
      setVerificationCode(value);
      
      // Auto-submit when code is complete
      if (value.length === 6) {
        handleVerify();
      }
    }
    
    if (formError) setFormError('');
    if (error) clearError();
  };
  
  // Handle method change
  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setVerificationCode('');
    if (formError) setFormError('');
    if (error) clearError();
    
    // Reset timer and resend code
    setTimer(120);
    setCanResend(false);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    
    // Send code via the selected method
    handleResendCode(newMethod);
  };
  
  // Handle verify
  const handleVerify = () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setFormError('Please enter the 6-digit verification code');
      return;
    }
    
    onVerify(verificationCode, method);
  };
  
  // Handle resend code
  const handleResendCode = (selectedMethod = method) => {
    onResendCode(selectedMethod);
    
    // Reset timer
    setTimer(120);
    setCanResend(false);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };
  
  return (
    <FormContainer>
      {/* Error message */}
      {(error || formError) && (
        <Fade in={Boolean(error || formError)}>
          <Alert 
            severity="error" 
            sx={{ width: '100%' }}
            onClose={() => formError ? setFormError('') : clearError()}
          >
            {formError || error}
          </Alert>
        </Fade>
      )}
      
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          For your security, we need to verify your identity.
          Please select a verification method and enter the code you receive.
        </Typography>
      </Box>
      
      {/* Verification method selection */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select verification method
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MethodButton
              fullWidth
              variant="outlined"
              selected={method === 'email'}
              onClick={() => handleMethodChange('email')}
              startIcon={<EmailIcon />}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Email
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Send code to {userEmail || 'your email'}
                </Typography>
              </Box>
            </MethodButton>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MethodButton
              fullWidth
              variant="outlined"
              selected={method === 'sms'}
              onClick={() => handleMethodChange('sms')}
              startIcon={<SmartphoneIcon />}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography variant="body2" fontWeight="bold">
                  SMS
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Send code to {maskedPhone}
                </Typography>
              </Box>
            </MethodButton>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Code input */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Enter verification code
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          We've sent a 6-digit code to your {method === 'email' ? 'email' : 'phone'}
        </Typography>
        
        <CodeInput
          inputRef={inputRef}
          fullWidth
          placeholder="123456"
          value={verificationCode}
          onChange={handleCodeChange}
          inputProps={{ maxLength: 6 }}
          sx={{ maxWidth: 300, mx: 'auto', my: 2 }}
          InputProps={{
            endAdornment: verificationCode && (
              <InputAdornment position="end">
                <IconButton onClick={() => setVerificationCode('')}>
                  <RestartAltIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        
        <TimerText variant="body2" warning={timer < 30}>
          {canResend ? 'Code expired' : `Code expires in ${formatTime(timer)}`}
        </TimerText>
        
        <Button
          variant="text"
          disabled={!canResend && !loading}
          onClick={() => handleResendCode()}
          sx={{ mt: 1 }}
        >
          Resend code
        </Button>
      </Box>
      
      {/* Verify button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleVerify}
        disabled={loading || verificationCode.length !== 6}
        sx={{ mt: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Verify'}
      </Button>
    </FormContainer>
  );
};

export default TwoFactorAuth;
